import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Search, Eye, IndianRupee, FileDown, Printer, MessageCircle, Mail, History } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "../api";
import ImportCsv from "../components/ImportCsv";
import Modal from "../components/Modal";
import PatientPicker from "../components/PatientPicker";
import TreatmentSelect from "../components/TreatmentSelect";
import { inputCls, labelCls, todayIso, toApiDate } from "../utils/datetime";

const STATUS_STYLE = {
  "Fully Paid": "bg-teal-soft text-teal-brand",
  "Partially Paid": "bg-gold-soft/70 text-pine-deep",
  Unpaid: "bg-red-50 text-red-500",
  Cancelled: "bg-slate-100 text-slate-500",
  Refunded: "bg-violet-50 text-violet-500",
};
const METHODS = ["Cash", "UPI", "Card", "Net Banking", "Insurance", "Other"];
const EMPTY_INV = { treatment: "", description: "", qty: "1", unit_price: "", discount: "0", tax: "0", doctor: "Dr. Ankita Pandey", date: todayIso(), notes: "" };

export default function Billing() {
  const [items, setItems] = useState([]);
  const [finance, setFinance] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_INV);
  const [patient, setPatient] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewInv, setViewInv] = useState(null);
  const [payInv, setPayInv] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", method: "Cash", reference: "", notes: "" });
  const [payBusy, setPayBusy] = useState(false);
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const canDelete = ["super_admin", "owner", "admin", "clinic_manager"].includes(me.level || "");
  const canEmail = ["super_admin", "owner", "admin", "clinic_manager", "front_desk", "assistant"].includes(me.level || "");

  const load = () => {
    api.get("/connect/invoices").then((r) => setItems(r.data)).catch(() => {});
    api.get("/connect/finance/summary").then((r) => setFinance(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/connect/treatments").then((r) => setTreatments(r.data || [])).catch(() => {}); }, []);

  const subtotal = (Number(form.qty) || 0) * (Number(form.unit_price) || 0);
  const total = Math.max(0, subtotal - (Number(form.discount) || 0) + (Number(form.tax) || 0));

  const pickTreatment = (name) => {
    const t = treatments.find((x) => x.name === name);
    setForm({ ...form, treatment: name, unit_price: t ? String(t.price) : form.unit_price });
  };

  const createInvoice = async (e) => {
    e.preventDefault();
    if (!patient) { toast.error("Select an existing patient — billing pulls their details automatically"); return; }
    setBusy(true);
    try {
      const r = await api.post("/connect/invoices", {
        patient_id: patient.patient_id,
        treatment: form.treatment || "General Consultation",
        description: form.description, qty: Number(form.qty) || 1,
        unit_price: Number(form.unit_price) || 0, discount: Number(form.discount) || 0, tax: Number(form.tax) || 0,
        doctor: form.doctor, date: toApiDate(form.date), notes: form.notes,
      });
      toast.success(`Invoice ${r.data.number} created for ${r.data.patient}`);
      setForm(EMPTY_INV); setPatient(null); setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the invoice");
    }
    setBusy(false);
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    setPayBusy(true);
    try {
      const r = await api.post(`/connect/invoices/${payInv.id}/payments`, {
        amount: Number(payForm.amount) || 0, method: payForm.method, reference: payForm.reference, notes: payForm.notes,
      });
      toast.success(`Payment recorded — ${r.data.payment_status}${r.data.balance > 0 ? ` · balance ${inr(r.data.balance)}` : ""}`);
      setPayInv(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record the payment");
    }
    setPayBusy(false);
  };

  const openView = async (inv) => {
    try {
      const r = await api.get(`/connect/invoices/${inv.id}`);
      setViewInv(r.data);
    } catch { toast.error("Could not load the invoice"); }
  };

  const downloadPdf = async (inv, print = false) => {
    try {
      const r = await api.get(`/connect/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (print) {
        const w = window.open(url, "_blank");
        if (w) w.addEventListener("load", () => w.print());
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = `${inv.number}.pdf`; a.click();
        URL.revokeObjectURL(url);
        toast.success(`Invoice ${inv.number} downloaded`);
      }
    } catch { toast.error("Could not generate the PDF"); }
  };

  const shareWhatsApp = async (inv) => {
    try {
      const r = await api.get(`/connect/invoices/${inv.id}/whatsapp`);
      if (r.data.status === "sent") { toast.success("Invoice sent on WhatsApp via API"); return; }
      if (r.data.status === "failed") { toast.error("WhatsApp API failed — opening share link instead"); }
      if (!r.data.wa_link) { toast.error("No phone number on this invoice"); return; }
      window.open(r.data.wa_link, "_blank");
      toast.info("WhatsApp opened — review the message and tap Send in WhatsApp");
    } catch { toast.error("Could not prepare the WhatsApp share"); }
  };

  const emailInvoice = async (inv) => {
    const to = inv.email || window.prompt("Patient email address:");
    if (!to) return;
    try {
      await api.post(`/connect/invoices/${inv.id}/email`, { to });
      toast.success(`Invoice emailed to ${to}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Email could not be sent"); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number}? Its payment history will also be removed.`)) return;
    try {
      await api.delete(`/connect/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      load();
    } catch { toast.error("Could not delete the invoice"); }
  };

  const filtered = useMemo(() => items.filter((i) => {
    if (statusFilter !== "all" && i.payment_status !== statusFilter) return false;
    const q = query.trim().toLowerCase();
    return !q || [i.number, i.patient, i.patient_id, i.phone, i.treatment].some((v) => String(v || "").toLowerCase().includes(q));
  }), [items, query, statusFilter]);

  const kpi = (label, value, filter, testid, tint = "text-pine-deep") => (
    <button key={label} type="button" data-testid={testid}
      onClick={() => setStatusFilter(filter)}
      className={`rounded-2xl bg-white border px-4 py-3 text-left shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-lift ${statusFilter === filter ? "border-teal-brand ring-1 ring-teal-brand/40" : "border-pine/8"}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</p>
      <p className={`mt-1 font-display text-lg font-semibold ${tint}`}>{value}</p>
    </button>
  );

  return (
    <div data-testid="connect-billing" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Billing &amp; Finance</h2>
          <p className="text-sm text-slate-500">Invoices, payments and balances — calculated from actual payment records.</p>
        </div>
        <div className="flex gap-2">
          <ImportCsv module="invoices" onDone={load} testid="invoices-import-csv" />
          <button data-testid="billing-add-toggle" onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 rounded-full bg-pine px-5 py-2.5 text-xs font-bold text-ivory shadow-lift hover:bg-pine-deep transition-all">
            <Plus className="h-4 w-4" /> New Invoice
          </button>
        </div>
      </div>

      {finance && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="finance-kpis">
          {kpi("Today's Revenue", inr(finance.today_revenue), "all", "kpi-today-revenue")}
          {kpi("Today's Collections", inr(finance.today_collections), "all", "kpi-today-collections", "text-teal-brand")}
          {kpi("Outstanding", inr(finance.outstanding), "Unpaid", "kpi-outstanding", "text-red-500")}
          {kpi("Unpaid", finance.status_counts?.Unpaid ?? 0, "Unpaid", "kpi-unpaid", "text-red-500")}
          {kpi("Partially Paid", finance.status_counts?.["Partially Paid"] ?? 0, "Partially Paid", "kpi-partial", "text-gold")}
          {kpi("Fully Paid", finance.status_counts?.["Fully Paid"] ?? 0, "Fully Paid", "kpi-paid", "text-teal-brand")}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="billing-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search invoice #, patient, PAT ID, phone, treatment…"
            className="h-11 w-full rounded-xl border border-pine/15 bg-white pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
        </div>
        <select data-testid="billing-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-11 rounded-xl border border-pine/15 bg-white px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine">
          {["all", "Unpaid", "Partially Paid", "Fully Paid", "Cancelled", "Refunded"].map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
        </select>
      </div>

      {showForm && (
        <form data-testid="billing-add-form" onSubmit={createInvoice} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Patient *</label>
              <div className="mt-1"><PatientPicker testid="invoice-patient-picker" value={patient} onSelect={setPatient} /></div>
            </div>
            {patient && (
              <div data-testid="invoice-patient-autofill" className="rounded-xl bg-teal-soft/60 border border-teal-brand/20 px-4 py-3 text-xs text-pine-deep self-end">
                <p className="font-bold">{patient.name} <span className="text-gold">{patient.patient_id}</span></p>
                <p className="mt-0.5 text-slate-600">{patient.phone}{patient.age ? ` · ${patient.age} yrs` : ""}{patient.gender ? ` · ${patient.gender}` : ""}{patient.email ? ` · ${patient.email}` : ""}</p>
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <label className={labelCls}>Treatment / Service *</label>
              <div className="mt-1"><TreatmentSelect testid="invoice-treatment" value={form.treatment} onChange={pickTreatment} /></div>
            </div>
            <div>
              <label className={labelCls}>Qty *</label>
              <input required type="number" min="1" inputMode="numeric" data-testid="invoice-qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Unit Price (₹) *</label>
              <input required type="number" min="0" inputMode="numeric" data-testid="invoice-unit-price" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Discount (₹)</label>
              <input type="number" min="0" inputMode="numeric" data-testid="invoice-discount" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Tax / GST (₹, manual)</label>
              <input type="number" min="0" inputMode="numeric" data-testid="invoice-tax" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Doctor</label>
              <input data-testid="invoice-doctor" value={form.doctor} onChange={(e) => setForm({ ...form, doctor: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className={labelCls}>Invoice Date</label>
              <input type="date" data-testid="invoice-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className={labelCls}>Notes</label>
              <input data-testid="invoice-notes" placeholder="Optional notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 rounded-xl bg-cream/70 px-4 py-3 text-sm">
            <span>Subtotal <b>{inr(subtotal)}</b></span>
            <span>Discount <b>− {inr(Number(form.discount) || 0)}</b></span>
            <span>Tax <b>+ {inr(Number(form.tax) || 0)}</b></span>
            <span data-testid="invoice-total-preview" className="font-display text-lg font-semibold text-pine-deep ml-auto">Total {inr(total)}</span>
          </div>
          <button type="submit" disabled={busy} data-testid="invoice-submit"
            className="h-11 rounded-full bg-teal-brand px-7 text-white text-xs font-bold hover:bg-pine transition-colors disabled:opacity-50">
            {busy ? "Creating…" : "Create Invoice"}
          </button>
        </form>
      )}

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-pine/8 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-5 py-3.5 font-bold">Invoice</th>
                <th className="px-5 py-3.5 font-bold">Patient</th>
                <th className="px-5 py-3.5 font-bold">Treatment</th>
                <th className="px-5 py-3.5 font-bold">Date</th>
                <th className="px-5 py-3.5 font-bold text-right">Total</th>
                <th className="px-5 py-3.5 font-bold text-right">Paid</th>
                <th className="px-5 py-3.5 font-bold text-right">Balance</th>
                <th className="px-5 py-3.5 font-bold">Status</th>
                <th className="px-5 py-3.5 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} data-testid={`invoice-row-${i.number}`} className="border-b border-pine/5 last:border-0 hover:bg-cream/50">
                  <td className="px-5 py-3.5 font-bold text-pine-deep">#{i.number}</td>
                  <td className="px-5 py-3.5 text-slate-600">{i.patient}{i.patient_id && <p className="text-[10px] font-bold text-gold">{i.patient_id}</p>}</td>
                  <td className="px-5 py-3.5 text-slate-600">{i.treatment}</td>
                  <td className="px-5 py-3.5 text-xs text-slate-500">{i.date}</td>
                  <td className="px-5 py-3.5 font-bold text-pine-deep text-right">{inr(i.total)}</td>
                  <td className="px-5 py-3.5 text-right text-teal-brand font-semibold">{inr(i.paid_total)}</td>
                  <td className={`px-5 py-3.5 text-right font-semibold ${i.balance > 0 ? "text-red-500" : "text-slate-400"}`}>{inr(i.balance)}</td>
                  <td className="px-5 py-3.5"><span className={`rounded-full px-3 py-1 text-[10px] font-bold ${STATUS_STYLE[i.payment_status] || STATUS_STYLE.Unpaid}`}>{i.payment_status}</span></td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5 justify-end">
                      <button data-testid={`invoice-view-${i.number}`} onClick={() => openView(i)} className="text-pine hover:text-teal-brand" aria-label="View invoice"><Eye className="h-4 w-4" /></button>
                      {i.balance > 0 && i.payment_status !== "Cancelled" && (
                        <button data-testid={`invoice-pay-${i.number}`} onClick={() => { setPayInv(i); setPayForm({ amount: String(i.balance), method: "Cash", reference: "", notes: "" }); }}
                          className="inline-flex items-center gap-1 rounded-full bg-teal-brand px-2.5 py-1 text-[10px] font-bold text-white hover:bg-pine" aria-label="Record payment">
                          <IndianRupee className="h-3 w-3" /> Pay
                        </button>
                      )}
                      <button data-testid={`invoice-pdf-${i.number}`} onClick={() => downloadPdf(i)} className="text-pine hover:text-teal-brand" aria-label="Download PDF"><FileDown className="h-4 w-4" /></button>
                      <button data-testid={`invoice-whatsapp-${i.number}`} onClick={() => shareWhatsApp(i)} className="text-[#25D366] hover:opacity-80" aria-label="Share on WhatsApp"><MessageCircle className="h-4 w-4" /></button>
                      {canDelete && (
                        <button data-testid={`invoice-delete-${i.number}`} onClick={() => remove(i)} className="text-red-500 hover:text-red-600" aria-label="Delete invoice"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-sm text-slate-400">No invoices found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {payInv && (
        <Modal testid="payment-modal" onClose={() => setPayInv(null)}
          title={`Record Payment — ${payInv.number}`}
          subtitle={`${payInv.patient} · Balance due ${inr(payInv.balance)}`}>
          <form onSubmit={recordPayment} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Amount (₹) *</label>
                <input required type="number" min="1" max={payInv.balance} inputMode="numeric" data-testid="payment-amount"
                  value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Method *</label>
                <select data-testid="payment-method" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className={`mt-1 ${inputCls}`}>
                  {METHODS.map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Transaction / Reference No.</label>
              <input data-testid="payment-reference" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className={`mt-1 ${inputCls}`} placeholder="UPI ref, cheque no. etc. (optional)" />
            </div>
            <div>
              <label className={labelCls}>Notes</label>
              <input data-testid="payment-notes" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className={`mt-1 ${inputCls}`} />
            </div>
            <p className="text-[11px] text-slate-400">Status updates automatically: partial amount → Partially Paid, full balance → Fully Paid.</p>
            <button type="submit" disabled={payBusy} data-testid="payment-submit"
              className="w-full rounded-full bg-teal-brand px-5 py-3 text-sm font-bold text-white hover:bg-pine transition-colors disabled:opacity-50">
              {payBusy ? "Recording…" : "Record Payment"}
            </button>
          </form>
        </Modal>
      )}

      {viewInv && (
        <Modal testid="invoice-view-modal" wide onClose={() => setViewInv(null)}
          title={`Invoice ${viewInv.number}`}
          subtitle={`${viewInv.patient}${viewInv.patient_id ? ` · ${viewInv.patient_id}` : ""} · ${viewInv.date}`}>
          <div className="rounded-xl bg-cream/60 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><p className="text-[10px] uppercase font-bold text-slate-400">Total</p><p className="font-bold text-pine-deep">{inr(viewInv.total)}</p></div>
            <div><p className="text-[10px] uppercase font-bold text-slate-400">Paid</p><p className="font-bold text-teal-brand">{inr(viewInv.paid_total)}</p></div>
            <div><p className="text-[10px] uppercase font-bold text-slate-400">Balance</p><p className={`font-bold ${viewInv.balance > 0 ? "text-red-500" : "text-slate-400"}`}>{inr(viewInv.balance)}</p></div>
            <div><p className="text-[10px] uppercase font-bold text-slate-400">Status</p><p className="font-bold text-pine-deep">{viewInv.payment_status}</p></div>
          </div>
          <div className="text-xs text-slate-600 space-y-1">
            <p><b>{viewInv.treatment}</b>{viewInv.description ? ` — ${viewInv.description}` : ""} · Qty {viewInv.qty} × {inr(viewInv.unit_price)}</p>
            <p>Subtotal {inr(viewInv.subtotal)}{viewInv.discount ? ` · Discount −${inr(viewInv.discount)}` : ""}{viewInv.tax ? ` · Tax +${inr(viewInv.tax)}` : ""} · Doctor: {viewInv.doctor || "-"}</p>
            {viewInv.phone && <p>Phone: {viewInv.phone}{viewInv.email ? ` · ${viewInv.email}` : ""}</p>}
            {viewInv.notes && <p className="italic text-slate-400">"{viewInv.notes}"</p>}
          </div>
          <div>
            <p className="text-xs font-bold text-pine/60 mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Payment History ({viewInv.payments?.length || 0})</p>
            <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {(viewInv.payments || []).map((p) => (
                <div key={p.id} data-testid={`payment-row-${p.payment_id}`} className="flex flex-wrap justify-between gap-2 rounded-xl border border-pine/8 px-4 py-2.5 text-xs">
                  <span className="font-bold text-pine-deep">{p.payment_id} {p.kind === "refund" && <span className="text-violet-500">(Refund)</span>}</span>
                  <span className="text-slate-500">{p.date} {p.time} · {p.method}{p.reference ? ` · ${p.reference}` : ""}</span>
                  <span className={`font-bold ${p.kind === "refund" ? "text-violet-500" : "text-teal-brand"}`}>{p.kind === "refund" ? "−" : ""}{inr(p.amount)}</span>
                </div>
              ))}
              {(viewInv.payments || []).length === 0 && <p className="text-xs text-slate-400">No payments recorded yet.</p>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button data-testid="invoice-download-btn" onClick={() => downloadPdf(viewInv)}
              className="inline-flex items-center gap-1.5 rounded-full bg-pine px-4 py-2.5 text-xs font-bold text-ivory hover:bg-pine-deep"><FileDown className="h-3.5 w-3.5" /> Download PDF</button>
            <button data-testid="invoice-print-btn" onClick={() => downloadPdf(viewInv, true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-pine/20 px-4 py-2.5 text-xs font-bold text-pine hover:bg-cream"><Printer className="h-3.5 w-3.5" /> Print</button>
            <button data-testid="invoice-whatsapp-btn" onClick={() => shareWhatsApp(viewInv)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2.5 text-xs font-bold text-white hover:opacity-90"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</button>
            {canEmail && (
              <button data-testid="invoice-email-btn" onClick={() => emailInvoice(viewInv)}
                className="inline-flex items-center gap-1.5 rounded-full border border-pine/20 px-4 py-2.5 text-xs font-bold text-pine hover:bg-cream"><Mail className="h-3.5 w-3.5" /> Email</button>
            )}
            {viewInv.balance > 0 && viewInv.payment_status !== "Cancelled" && (
              <button data-testid="invoice-record-pay-btn" onClick={() => { setPayInv(viewInv); setPayForm({ amount: String(viewInv.balance), method: "Cash", reference: "", notes: "" }); }}
                className="inline-flex items-center gap-1.5 rounded-full bg-teal-brand px-4 py-2.5 text-xs font-bold text-white hover:bg-pine"><IndianRupee className="h-3.5 w-3.5" /> Record Payment</button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
